import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LockSquare, Eye, EyeClosed, ArrowRight } from "iconoir-react";
import { api, saveActiveHousehold } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function ResetPassword() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { setUser } = useAuth();

    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, new_password: password });
            if (data.session_token) localStorage.setItem("milo_session_token", data.session_token);
            setUser(data.user);
            toast.success("Password updated — you're signed in");
            // try skip picker
            try {
                const { data: list } = await api.get("/me/households");
                if (list.length === 1) {
                    const e0 = list[0];
                    saveActiveHousehold({
                        household_id: e0.household.id,
                        household_name: e0.household.name,
                        household_code: e0.household.code,
                        member_id: e0.member.id,
                        member_name: e0.member.name,
                    });
                    nav("/app", { replace: true });
                    return;
                }
            } catch (e) { console.warn("pages/ResetPassword.jsx:", e); }
            nav("/picker", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed — the link may be expired");
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8">
                <div className="brut-card p-6 max-w-md text-center">
                    <div className="font-heading text-xl font-black mb-2">Invalid link</div>
                    <div className="text-sm text-[#255636] mb-4">This reset link is missing or malformed.</div>
                    <Link to="/forgot-password" className="brut-btn">Request a new link</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center p-4 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-[#F3D469] rounded-full opacity-60 blur-2xl" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
            >
                <div className="mb-6 flex flex-col items-center text-center">
                    <div className="bg-white brut-border brut-shadow rounded-3xl px-6 py-4 mb-4">
                        <img src="/milo-logo.png" alt="Milo" className="h-20 w-auto object-contain" />
                    </div>
                    <div className="font-heading text-3xl font-black tracking-tight">Set a new password</div>
                </div>
                <div className="brut-card p-6 sm:p-7">
                    <form onSubmit={submit} className="space-y-3">
                        <label className="block">
                            <div className="text-xs uppercase tracking-widest font-bold mb-1.5 flex items-center gap-2">
                                <LockSquare className="w-3.5 h-3.5" /> New password
                            </div>
                            <div className="relative">
                                <input
                                    data-testid="reset-password-input"
                                    type={showPw ? "text" : "password"}
                                    required
                                    minLength={6}
                                    className="brut-input pr-11"
                                    placeholder="At least 6 characters"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center opacity-60 hover:opacity-100"
                                >
                                    {showPw ? <EyeClosed className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="reset-submit-btn"
                            className="brut-btn w-full justify-center text-base"
                        >
                            {busy ? "Saving…" : "Set password & sign in"} <ArrowRight className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}
